import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Search,
  AlertCircle,
  Loader2,
  MessageSquare,
  X,
  RefreshCw,
  User,
  Send,
  Phone,
  Mail,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getAllTickets,
  getTicketByIdAdmin,
  updateTicketStatus,
  updateTicketCategory,
  TicketStatus,
  TicketType,
  TicketSubtype,
  TICKET_SUBTYPES_BY_TYPE,
  getStatusLabel,
  getStatusColor,
  getTypeLabel,
  getSubtypeLabel,
  formatDate,
  getRelativeTime,
  validateStatusUpdate,
} from "../../../api/ticketApi";
import { useSnapshot } from "valtio";
import { permissionStore, PermissionAction, ResourceType } from "../../../stores/permissionStore";

export default function ManageTickets() {
  const permSnap = useSnapshot(permissionStore);
  const [activeTab, setActiveTab] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  
  // Permission checks
  const canReadTicket = permSnap.can(PermissionAction.READ, ResourceType.SUPPORT_TICKET);
  const canUpdateTicket = permSnap.can(PermissionAction.UPDATE, ResourceType.SUPPORT_TICKET);
  const [ticketSummary, setTicketSummary] = useState({
    [TicketStatus.PENDING]: 0,
    [TicketStatus.IN_PROGRESS]: 0,
    [TicketStatus.RESOLVED]: 0,
    [TicketStatus.CLOSED]: 0,
  });
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 8,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [error, setError] = useState(null);

  // User Profile Modal State
  const [userProfileModal, setUserProfileModal] = useState({
    isOpen: false,
    user: null,
  });

  // Reply Modal State
  const [replyModal, setReplyModal] = useState({
    isOpen: false,
    ticket: null,
    replyMessage: "",
    newStatus: TicketStatus.PENDING,
    isSubmitting: false,
    error: "",
  });

  // Ticket detail modal state
  const [ticketDetailModal, setTicketDetailModal] = useState({
    isOpen: false,
    ticket: null,
    isLoading: false,
    editType: TicketType.TECHNICAL,
    editSubtype: TicketSubtype.VIDEO_PLAYBACK,
    isUpdatingCategory: false,
    categoryError: "",
  });

  // Filter tickets based on active tab
  const getFilteredTickets = () => {
    if (activeTab === "All") return tickets;
    return tickets.filter((ticket) => ticket.status === activeTab);
  };

  // Fetch tickets function
  const fetchTickets = async (filters = {}) => {
    if (!canReadTicket) {
      setTickets([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      const result = await getAllTickets({
        status: activeTab === "All" ? undefined : activeTab,
        type: typeFilter === "All" ? undefined : typeFilter,
        search: searchQuery || undefined,
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });

      setTickets(result.tickets);
      setPagination(result.pagination);
      setTicketSummary(result.summary);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to fetch tickets"
      );
      setTickets([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Handle search with debounce
  const handleSearch = (searchValue = searchQuery) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }

    if (searchValue || searchValue === "") {
      const timerId = setTimeout(() => {
        setPagination((prev) => ({ ...prev, page: 1 }));
        fetchTickets({ search: searchValue || undefined });
      }, 300);

      setSearchTimeout(timerId);
    }
  };

  // Initialize and fetch tickets
  useEffect(() => {
    fetchTickets();
  }, [activeTab, typeFilter, pagination.page]);

  // Handle search input changes. Skipped on the first render — the effect
  // above already does the initial fetch, and running both duplicated every
  // page load.
  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    handleSearch(searchQuery);
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchQuery]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Handle tab change
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSelectedTickets([]);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleTypeChange = (value) => {
    setTypeFilter(value);
    setSelectedTickets([]);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle refresh
  const handleRefresh = async (e) => {
    e?.preventDefault();
    setSearchQuery("");
    setSelectedTickets([]);
    setPagination((prev) => ({ ...prev, page: 1 }));
    await fetchTickets();
  };

  // Handle user profile click
  const handleUserProfileClick = (user) => {
    setUserProfileModal({
      isOpen: true,
      user,
    });
  };

  // Handle reply to ticket
  const handleReplyToTicket = (ticket) => {
    setReplyModal({
      isOpen: true,
      ticket,
      replyMessage: "",
      newStatus:
        ticket.status === TicketStatus.PENDING
          ? TicketStatus.IN_PROGRESS
          : ticket.status,
      isSubmitting: false,
      error: "",
    });
  };

  // Submit reply
  const submitReply = async () => {
    if (!canUpdateTicket) {
      setReplyModal((prev) => ({
        ...prev,
        error: "You don't have permission to update tickets",
      }));
      return;
    }
    
    if (!replyModal.ticket || !replyModal.replyMessage.trim()) {
      setReplyModal((prev) => ({
        ...prev,
        error: "Please enter a reply message",
      }));
      return;
    }

    const updateData = {
      status: replyModal.newStatus,
      statusUpdate: replyModal.replyMessage.trim(),
    };

    const validationErrors = validateStatusUpdate(updateData);
    if (validationErrors.length > 0) {
      setReplyModal((prev) => ({
        ...prev,
        error: validationErrors.join(", "),
      }));
      return;
    }

    setReplyModal((prev) => ({ ...prev, isSubmitting: true, error: "" }));

    try {
      await updateTicketStatus(replyModal.ticket.id, updateData);
      setReplyModal({
        isOpen: false,
        ticket: null,
        replyMessage: "",
        newStatus: TicketStatus.PENDING,
        isSubmitting: false,
        error: "",
      });
      await fetchTickets(); // Refresh tickets
    } catch (err) {
      setReplyModal((prev) => ({
        ...prev,
        error:
          err.response?.data?.message || err.message || "Failed to send reply",
        isSubmitting: false,
      }));
    }
  };

  // Handle view ticket details
  const handleViewDetails = async (ticketId) => {
    setTicketDetailModal({
      isOpen: true,
      ticket: null,
      isLoading: true,
      editType: TicketType.TECHNICAL,
      editSubtype: TicketSubtype.VIDEO_PLAYBACK,
      isUpdatingCategory: false,
      categoryError: "",
    });

    try {
      const ticket = await getTicketByIdAdmin(ticketId);
      const type =
        ticket.type === TicketType.ACCOUNT ||
        ticket.type === TicketType.TECHNICAL
          ? ticket.type
          : TicketType.TECHNICAL;
      const subtypes = TICKET_SUBTYPES_BY_TYPE[type] || [];
      const subtype = subtypes.some((s) => s.value === ticket.subtype)
        ? ticket.subtype
        : subtypes[0]?.value;

      setTicketDetailModal((prev) => ({
        ...prev,
        ticket,
        editType: type,
        editSubtype: subtype,
        isLoading: false,
      }));
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch ticket details"
      );
      setTicketDetailModal((prev) => ({
        ...prev,
        isOpen: false,
        isLoading: false,
      }));
    }
  };

  const handleCategoryTypeChange = (type) => {
    const subtypes = TICKET_SUBTYPES_BY_TYPE[type] || [];
    setTicketDetailModal((prev) => ({
      ...prev,
      editType: type,
      editSubtype: subtypes[0]?.value,
      categoryError: "",
    }));
  };

  const submitCategoryUpdate = async () => {
    if (!canUpdateTicket || !ticketDetailModal.ticket) return;

    const { ticket, editType, editSubtype } = ticketDetailModal;
    if (
      ticket.type === editType &&
      ticket.subtype === editSubtype
    ) {
      return;
    }

    setTicketDetailModal((prev) => ({
      ...prev,
      isUpdatingCategory: true,
      categoryError: "",
    }));

    try {
      const updated = await updateTicketCategory(ticket.id, {
        type: editType,
        subtype: editSubtype,
      });
      setTicketDetailModal((prev) => ({
        ...prev,
        ticket: updated,
        editType: updated.type,
        editSubtype: updated.subtype,
        isUpdatingCategory: false,
      }));
      await fetchTickets();
    } catch (err) {
      setTicketDetailModal((prev) => ({
        ...prev,
        isUpdatingCategory: false,
        categoryError:
          err.response?.data?.message ||
          err.message ||
          "Failed to update category",
      }));
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const filteredTickets = getFilteredTickets();

  // If user doesn't have READ permission, show message
  if (!canReadTicket) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view support tickets.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 pt-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Manage Tickets</h1>
          <p className="text-muted-foreground">
            View and manage support tickets from users
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <span className="text-red-800">{error}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
              className="ml-auto"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search for tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={handleTypeChange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Categories</SelectItem>
              <SelectItem value={TicketType.ACCOUNT}>Account</SelectItem>
              <SelectItem value={TicketType.TECHNICAL}>Technical</SelectItem>
            </SelectContent>
          </Select>

          <Select value={activeTab} onValueChange={handleTabChange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All</SelectItem>
              <SelectItem value={TicketStatus.PENDING}>Pending</SelectItem>
              <SelectItem value={TicketStatus.IN_PROGRESS}>
                In Progress
              </SelectItem>
              <SelectItem value={TicketStatus.RESOLVED}>Resolved</SelectItem>
              <SelectItem value={TicketStatus.CLOSED}>Closed</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tickets List */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTickets.length > 0 ? (
            filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white border rounded-lg p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    {/* Ticket Header */}
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        {ticket?.user?.profileImage ? (
                          <div className="rounded-md text-white w-8 h-8 flex items-center justify-center font-bold">
                            <img
                              src={import.meta.env.VITE_CDN_LINK + ticket.user.profileImage}
                              alt="Profile"
                              className="w-full h-full object-cover object-center rounded-md"
                            />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full text-sm">
                            <div className="w-full h-full flex items-center justify-center bg-[#5D93A5] rounded-full text-white font-medium text-lg">
                              {getInitials(ticket.user?.name || "User")}
                            </div>
                          </div>
                        )}
                        <button
                          onClick={() => handleUserProfileClick(ticket.user)}
                          className="text-sm font-medium hover:text-blue-600 transition-colors"
                        >
                          {ticket.user?.name || "Unknown User"}
                        </button>
                      </div>
                      <span className="text-sm text-gray-500">•</span>
                      <span className="text-sm text-gray-500">
                        {formatDate(ticket.createdAt)}
                      </span>
                    </div>

                    {/* Ticket Title and Content */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <img
                          src="/label_important.svg"
                          alt="User Avatar"
                          className="w-5 h-5 rounded-full"
                        />
                        <h3 className="font-medium text-gray-900">
                          {ticket.title}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {ticket.description}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {getTypeLabel(ticket.type)}
                      </Badge>
                      <Badge
                        className="text-xs"
                        style={{
                          backgroundColor: getStatusColor(ticket.status),
                          color: "white",
                        }}
                      >
                        {getStatusLabel(ticket.status)}
                      </Badge>
                    </div>
                    {canReadTicket && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(ticket.id)}
                      >
                        View Details
                      </Button>
                    )}
                   {canUpdateTicket && ticket.status !== "RESOLVED" && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleReplyToTicket(ticket)}
                        className="bg-[#5D93A5] text-white"
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Reply
                      </Button>
                    )}
                    {/* <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => handleViewDetails(ticket.id)}
                        >
                          <MessageSquare className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleReplyToTicket(ticket)}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          Reply
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu> */}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {searchQuery
                  ? `No tickets match your search for "${searchQuery}"`
                  : `No ${activeTab === "All" ? "" : activeTab.toLowerCase()} tickets found`}
              </p>
              {searchQuery && (
                <Button
                  variant="ghost"
                  className="mt-2"
                  onClick={() => setSearchQuery("")}
                >
                  Clear search
                </Button>
              )}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t pt-4">
              <div className="text-sm text-gray-500">
                {pagination.total > 0 ? (
                  <>
                    {Math.min(
                      (pagination.page - 1) * pagination.limit + 1,
                      pagination.total
                    )}
                    -
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total}
                  </>
                ) : (
                  "0 of 0"
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev}
                >
                  Previous
                </Button>
                <span className="text-sm px-2">
                  {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* User Profile Modal */}
      <Dialog
        open={userProfileModal.isOpen}
        onOpenChange={(open) =>
          setUserProfileModal((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <DialogContent className="sm:max-w-[350px]">
          <DialogHeader>
            <DialogTitle>Profile</DialogTitle>
            <button
              onClick={() =>
                setUserProfileModal((prev) => ({ ...prev, isOpen: false }))
              }
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogHeader>

          {userProfileModal.user && (
            <div className="space-y-6 py-4">
              {/* User Avatar and Name */}
              <div className="text-center space-y-2">
                <div className="w-16 h-16 bg-[#5D93A5] text-white text-3xl rounded-full flex items-center justify-center mx-auto">
                  {userProfileModal.user.imageUrl ? (
                    <img
                      src={
                        import.meta.env.VITE_CDN_LINK +
                        userProfileModal.user.imageUrl
                      }
                      alt={userProfileModal.user.name}
                      className="w-16 h-16 rounded-full"
                    />
                  ) : (
                    getInitials(userProfileModal.user.name)
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    @
                    {userProfileModal.user.name?.toLowerCase().replace(" ", "")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {userProfileModal.user.rankDesignation || "User"}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 my-2" />

              {/* User Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">CDC:</span>
                  <p className="font-medium">
                    {userProfileModal.user.indianCdc || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Gender:</span>
                  <p className="font-medium">
                    {userProfileModal.user.gender || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">IMO:</span>
                  <p className="font-medium">
                    {userProfileModal.user.imo || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">SID:</span>
                  <p className="font-medium">
                    {userProfileModal.user.sea ||
                      userProfileModal.user.indianCdc ||
                      "N/A"}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 my-2" />

              {/* Contact Information */}
              <div className="space-y-3">
                {userProfileModal.user.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-[#5D93A5]" />
                    <span>{userProfileModal.user.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="h-4 w-4 text-[#5D93A5]" />
                  <span>{userProfileModal.user.email}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reply Modal */}
      <Dialog
        open={replyModal.isOpen}
        onOpenChange={(open) =>
          !replyModal.isSubmitting &&
          setReplyModal((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Reply to Ticket</DialogTitle>
            <DialogDescription>
              Send a reply to "{replyModal.ticket?.title}" and update the ticket
              status.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Original Ticket Info */}
            {replyModal.ticket && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium">{replyModal.ticket.title}</h4>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {replyModal.ticket.description}
                </p>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline">
                    {getTypeLabel(replyModal.ticket.type)}
                  </Badge>
                  <Badge
                    style={{
                      backgroundColor: getStatusColor(replyModal.ticket.status),
                      color: "white",
                    }}
                  >
                    {getStatusLabel(replyModal.ticket.status)}
                  </Badge>
                </div>
              </div>
            )}

            {/* Reply Message */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Your Reply</label>
              <Textarea
                placeholder="Type your message here..."
                value={replyModal.replyMessage}
                onChange={(e) =>
                  setReplyModal((prev) => ({
                    ...prev,
                    replyMessage: e.target.value,
                  }))
                }
                className="min-h-[100px] resize-none"
                disabled={replyModal.isSubmitting}
              />
            </div>

            {/* Status Update */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Update Status</label>
              <Select
                value={replyModal.newStatus}
                onValueChange={(value) =>
                  setReplyModal((prev) => ({ ...prev, newStatus: value }))
                }
                disabled={replyModal.isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TicketStatus.PENDING}>Pending</SelectItem>
                  <SelectItem value={TicketStatus.IN_PROGRESS}>
                    In Progress
                  </SelectItem>
                  <SelectItem value={TicketStatus.RESOLVED}>
                    Resolved
                  </SelectItem>
                  <SelectItem value={TicketStatus.CLOSED}>Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {replyModal.error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                {replyModal.error}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              variant="outline"
              onClick={() =>
                setReplyModal((prev) => ({ ...prev, isOpen: false }))
              }
              disabled={replyModal.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={submitReply}
              disabled={
                replyModal.isSubmitting || !replyModal.replyMessage.trim()
              }
              className="text-white bg-[#5D93A5]"
            >
              {replyModal.isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              <Send className="mr-2 h-4 w-4" />
              Send Reply
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Modal */}
      <Dialog
        open={ticketDetailModal.isOpen}
        onOpenChange={(open) =>
          setTicketDetailModal((prev) => ({ ...prev, isOpen: open }))
        }
      >
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ticket Details</DialogTitle>
          </DialogHeader>
          {ticketDetailModal.isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : ticketDetailModal.ticket ? (
            <div className="space-y-6 py-4">
              {/* Ticket Header */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-2">
                  {ticketDetailModal.ticket.title}
                </h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    {getTypeLabel(ticketDetailModal.ticket.type)}
                  </Badge>
                  <Badge
                    style={{
                      backgroundColor: getStatusColor(
                        ticketDetailModal.ticket.status
                      ),
                      color: "white",
                    }}
                  >
                    {getStatusLabel(ticketDetailModal.ticket.status)}
                  </Badge>
                  {ticketDetailModal.ticket.subtype && (
                    <Badge variant="secondary">
                      {getSubtypeLabel(ticketDetailModal.ticket.subtype)}
                    </Badge>
                  )}
                </div>
              </div>

              {canUpdateTicket && (
                <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
                  <label className="text-sm font-medium text-gray-700 block">
                    Update Category
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">
                        Category
                      </label>
                      <Select
                        value={ticketDetailModal.editType}
                        onValueChange={handleCategoryTypeChange}
                        disabled={ticketDetailModal.isUpdatingCategory}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={TicketType.ACCOUNT}>
                            Account
                          </SelectItem>
                          <SelectItem value={TicketType.TECHNICAL}>
                            Technical
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">
                        Sub-category
                      </label>
                      <Select
                        value={ticketDetailModal.editSubtype}
                        onValueChange={(value) =>
                          setTicketDetailModal((prev) => ({
                            ...prev,
                            editSubtype: value,
                            categoryError: "",
                          }))
                        }
                        disabled={ticketDetailModal.isUpdatingCategory}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(
                            TICKET_SUBTYPES_BY_TYPE[
                              ticketDetailModal.editType
                            ] || []
                          ).map((sub) => (
                            <SelectItem key={sub.value} value={sub.value}>
                              {sub.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {ticketDetailModal.categoryError && (
                    <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                      {ticketDetailModal.categoryError}
                    </div>
                  )}
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      className="bg-[#5D93A5] text-white"
                      onClick={submitCategoryUpdate}
                      disabled={
                        ticketDetailModal.isUpdatingCategory ||
                        (ticketDetailModal.ticket.type ===
                          ticketDetailModal.editType &&
                          ticketDetailModal.ticket.subtype ===
                            ticketDetailModal.editSubtype)
                      }
                    >
                      {ticketDetailModal.isUpdatingCategory && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Save Category
                    </Button>
                  </div>
                </div>
              )}

              {/* Ticket Content */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {ticketDetailModal.ticket.description}
                  </p>
                </div>

                {ticketDetailModal.ticket.extraInfo && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Additional Information
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {ticketDetailModal.ticket.extraInfo}
                    </p>
                  </div>
                )}

                {/* User Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Submitted By
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      {ticketDetailModal.ticket.user?.profileImage ? (
                        <div className="rounded-md text-white w-10 h-10 flex items-center justify-center font-bold">
                          <img
                            src={ import.meta.env.VITE_CDN_LINK + ticketDetailModal.ticket.user.profileImage}
                            alt="Profile"
                            className="w-full h-full object-cover object-center rounded-md"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full text-sm">
                          <div className="w-full h-full flex items-center justify-center bg-[#5D93A5] rounded-full text-white font-medium text-lg">
                            {getInitials(ticketDetailModal.ticket.user.name)}
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {ticketDetailModal.ticket.user?.name || "Unknown User"}
                      </p>
                      <p className="text-xs text-gray-600">
                        {ticketDetailModal.ticket.user?.email}
                      </p>
                      <p className="text-xs text-gray-600">
                        {ticketDetailModal.ticket.user?.rankDesignation}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-gray-700 font-medium">Created</label>
                    <p className="text-gray-900">
                      {formatDate(ticketDetailModal.ticket.createdAt)}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-700 font-medium">
                      Last Updated
                    </label>
                    <p className="text-gray-900">
                      {getRelativeTime(ticketDetailModal.ticket.updatedAt)}
                    </p>
                  </div>
                </div>
                {ticketDetailModal.ticket.statusUpdate && (
                  <div className="border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MessageSquare className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Status Update
                          </h4>
                          <div className="flex items-center gap-2">
                            <Badge
                              className="text-xs"
                              style={{
                                backgroundColor: getStatusColor(
                                  ticketDetailModal.ticket.status
                                ),
                                color: "white",
                              }}
                            >
                              {getStatusLabel(ticketDetailModal.ticket.status)}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {getRelativeTime(
                                ticketDetailModal.ticket.updatedAt
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                          <p className="text-sm text-gray-800 leading-relaxed">
                            {ticketDetailModal.ticket.statusUpdate}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">Failed to load ticket details</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
